/**
 * Listing marketing (listing-marketing-hub) — the Marketing tab's copy kit and photo download.
 * Endpoints: apps/api/src/routes/manager/listing-marketing.routes.ts (landlord session).
 */
import type { ListingMarketingExportDto } from '@resida/api-types/listing-extension';
import { apiFetch, apiFetchFile } from './api.core';

export type { ListingMarketingExportDto };

/** Query key for a listing's marketing copy kit (DC-7). */
export const marketingExportKey = (listingId: string) => ['listing', listingId, 'marketing-export'] as const;

/** DC-7: title, description, public link and Resida email for any ever-activated listing. */
export function fetchMarketingExport(listingId: string): Promise<ListingMarketingExportDto> {
  return apiFetch<ListingMarketingExportDto>(`/landlord/api/listings/${listingId}/marketing-export`);
}

/**
 * DC-6: the zip of approved photos in display order. Fetched (not a plain download link) so a failed
 * archive (no approved photos left, rate limit, network) surfaces as an error in the app instead of
 * the browser saving the error body as a file; on success the zip is saved under the server's file name.
 */
export async function downloadPhotosArchive(listingId: string): Promise<void> {
  const response = await apiFetchFile(`/landlord/api/listings/${listingId}/photos-archive`);
  const blob = await response.blob();
  const fileName = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'listing-photos.zip';
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser time to start reading the blob before releasing it.
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
